import type {PointerEvent as ReactPointerEvent, ReactElement} from 'react';
import {useEffect, useRef} from 'react';
import {useImageCropMode} from '@EmailMakerReact/hooks/use-image-crop-mode';
import {setImageCropWindow} from '@EmailMakerReact/lib/email-maker-image-crop-library';
import {CROP_CORNER_BRACKET_PATH, CROP_CORNER_BRACKET_VIEW_BOX} from '@Libraries/crop-handle-glyph.library';
import {getCropWindowInFrameBox, getCropWindowInImageBox, getDraggedCropWindow, getImageCropScale, getVisibleCropWindowHandleSpecs} from './image-crop-overlay.library';
import type {CropWindowDrag, CropWindowDragStart, CropWindowHandleSpec, ImageCropOverlayProps} from './image-crop-overlay.types';
import {CropWindowDragKind} from './image-crop-overlay.types';
import {useImageCropOverlayGeometry} from './use-image-crop-overlay-geometry';
import styles from './image-crop-overlay.module.scss';

export function ImageCropOverlay({editor}: ImageCropOverlayProps): ReactElement | null {
  const cropModeState = useImageCropMode(editor);
  const layerRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<CropWindowDrag | null>(null);
  const geometry = useImageCropOverlayGeometry(editor, layerRef, cropModeState);

  useEffect(() => {
    dragRef.current = null;
  }, [cropModeState?.component]);

  if (!cropModeState) {
    return null;
  }
  if (!geometry) {
    return <div className={styles.imageCropLayer} ref={layerRef} />;
  }

  const scale = getImageCropScale(geometry, cropModeState);

  const startDrag = (event: ReactPointerEvent<HTMLDivElement>, drag: CropWindowDrag): void => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = drag;
  };

  const getDragStart = (event: ReactPointerEvent<HTMLDivElement>): CropWindowDragStart => {
    return {
      pointerId: event.pointerId,
      startPointer: {x: event.clientX, y: event.clientY},
      startCropWindow: cropModeState.cropWindow,
      scale,
    };
  };

  const onWindowPointerDown = (event: ReactPointerEvent<HTMLDivElement>): void => {
    startDrag(event, {...getDragStart(event), kind: CropWindowDragKind.MOVE});
  };

  const onHandlePointerDown = (event: ReactPointerEvent<HTMLDivElement>, spec: CropWindowHandleSpec): void => {
    startDrag(event, {...getDragStart(event), kind: CropWindowDragKind.RESIZE, spec});
  };

  const onWindowPointerMove = (event: ReactPointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current;
    if (drag?.pointerId !== event.pointerId || event.buttons === 0) {
      return;
    }
    event.stopPropagation();
    setImageCropWindow(editor, getDraggedCropWindow(drag, {x: event.clientX, y: event.clientY}, cropModeState));
  };

  const onWindowPointerEnd = (event: ReactPointerEvent<HTMLDivElement>): void => {
    event.stopPropagation();
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null;
    }
  };

  const renderHandle = (spec: CropWindowHandleSpec): ReactElement => {
    return (
      <div
        key={spec.handle}
        className={`${styles.cropHandle} ${spec.isCorner ? styles.cornerHandle : styles.edgeHandle} ${styles[spec.position]}`}
        onPointerDown={(event): void => {
          onHandlePointerDown(event, spec);
        }}
      >
        {renderCornerBracket(spec)}
      </div>
    );
  };

  return (
    <div className={styles.imageCropLayer} ref={layerRef}>
      <div className={styles.cropFrameViewport} style={geometry.frameBox}>
        <div className={styles.cropFrameClip} style={geometry.frameClipBox}>
          <div className={styles.cropImageBox} style={geometry.imageBox}>
            <div className={styles.cropScrimWindow} style={getCropWindowInImageBox(cropModeState.cropWindow, scale)} />
          </div>
        </div>
        <div
          role="presentation"
          className={styles.cropWindow}
          style={getCropWindowInFrameBox(geometry, cropModeState.cropWindow, scale)}
          onPointerDown={onWindowPointerDown}
          onPointerMove={onWindowPointerMove}
          onPointerUp={onWindowPointerEnd}
          onPointerCancel={onWindowPointerEnd}
          onLostPointerCapture={onWindowPointerEnd}
        >
          {getVisibleCropWindowHandleSpecs(cropModeState.aspectRatio).map(renderHandle)}
        </div>
      </div>
    </div>
  );
}

function renderCornerBracket(spec: CropWindowHandleSpec): ReactElement | null {
  if (!spec.isCorner) {
    return null;
  }
  return (
    <svg className={styles.cornerBracket} viewBox={CROP_CORNER_BRACKET_VIEW_BOX} aria-hidden="true">
      <path className={styles.cornerBracketBorder} d={CROP_CORNER_BRACKET_PATH} />
      <path className={styles.cornerBracketBar} d={CROP_CORNER_BRACKET_PATH} />
    </svg>
  );
}
